import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BarChart3, CalendarDays, TrendingUp, Zap, GitCompare, History, LineChart, LayoutGrid, ArrowRight, Lightbulb, MapPin } from "lucide-react";
import Strategy from "@/pages/Strategy";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { isGeneralManager } from "@/lib/roles";
import BigPicture from "@/pages/BigPicture";
import DailyReport from "@/pages/DailyReport";
import CommitteeAnalytics from "@/pages/CommitteeAnalytics";
import Efficiency from "@/pages/Efficiency";
import Compare from "@/pages/Compare";
import ComparisonTimeline from "@/pages/ComparisonTimeline";
import SicilDistribution from "@/pages/SicilDistribution";

const TABS = [
  {
    slug: "strateji", label: "Strateji Danışmanı", icon: Lightbulb,
    desc: "Durum yorumu, riskler ve firma bazlı öneriler",
    roles: ["super_admin", "committee_manager", "branch_manager"], Comp: Strategy,
  },
  {
    slug: "gunluk-rapor", label: "Günlük Rapor", icon: CalendarDays,
    desc: "Bugün ne oldu?", roles: ["super_admin", "committee_manager", "branch_manager"],
    Comp: DailyReport,
  },
  {
    slug: "buyuk-resim", label: "Genel Bakış", icon: TrendingUp,
    desc: "Büyük resim ve trendler", roles: ["super_admin", "committee_manager"],
    Comp: BigPicture,
  },
  {
    slug: "komite-analizleri", label: "Komite Analizleri", icon: LineChart,
    desc: "Komite bazında performans", roles: ["super_admin", "committee_manager"],
    Comp: CommitteeAnalytics,
  },
  {
    slug: "verimlilik", label: "Meclis Üyesi Verimliliği", icon: Zap,
    desc: "61 koltuk hesabı", roles: ["super_admin", "committee_manager", "chairman"],
    gmOnly: true, Comp: Efficiency,
  },
  {
    slug: "kiyasla", label: "Karşılaştır", icon: GitCompare,
    desc: "Komiteleri kıyasla", roles: ["super_admin", "committee_manager", "chairman", "branch_manager"],
    Comp: Compare,
  },
  {
    slug: "zaman-tuneli", label: "Zaman Tüneli", icon: History,
    desc: "Dönemler arası değişim",
    roles: ["super_admin", "committee_manager", "field_agent", "chairman", "branch_manager"],
    Comp: ComparisonTimeline,
  },
  {
    slug: "sicil-ili", label: "Sicil İli Dağılımı", icon: MapPin,
    desc: "Hangi sicil ilinde kaç firma ve kesin oy var",
    roles: ["super_admin", "committee_manager", "chairman", "branch_manager"],
    Comp: SicilDistribution,
  },
];

export const analyticsTabsFor = (user) =>
  TABS.filter((t) => t.roles.includes(user?.role) && (!t.gmOnly || isGeneralManager(user)));

function Overview({ tabs, onOpen }) {
  const [sc, setSc] = useState(null);
  const [tg, setTg] = useState(null);
  const [today, setToday] = useState(null);

  useEffect(() => {
    api.get("/dashboard/committee-scorecard").then(({ data }) => setSc(data.rows || [])).catch(() => setSc([]));
    api.get("/reports/targets").then(({ data }) => setTg(data)).catch(() => setTg(null));
    api.get("/reports/daily", { params: { days: 1 } }).then(({ data }) => setToday(data.totals)).catch(() => setToday(null));
  }, []);

  const t = useMemo(() => {
    const rows = sc || [];
    const o = { seats: 0, wonSeats: 0, won: 0, bize: 0, karsi: 0, health: 0, n: rows.length };
    rows.forEach((r) => {
      const m = r.field || {};
      o.seats += r.council_seats || 0;
      o.bize += m.bize || 0;
      o.karsi += m.karsi || 0;
      o.health += r.health || 0;
      if (m.winning) { o.won += 1; o.wonSeats += r.council_seats || 0; }
    });
    o.avgHealth = o.n ? Math.round(o.health / o.n) : 0;
    o.majority = o.seats ? Math.floor(o.seats / 2) + 1 : 61;
    return o;
  }, [sc]);

  const STATS = {
    strateji: sc && tg ? [
      { l: "Durum", v: tg.votes.behind || tg.belge.behind ? "Geride" : "Hedefte", good: !(tg.votes.behind || tg.belge.behind) },
      { l: "Eksik oy", v: tg.votes.gap, good: false }, { l: "Eksik belge", v: tg.belge.gap, good: false },
    ] : null,
    "gunluk-rapor": today ? [
      { l: "Bugün net oy", v: today.net > 0 ? `+${today.net}` : `${today.net}`, good: today.net >= 0 },
      { l: "Alınan belge", v: today.belge }, { l: "Arama / Not", v: today.calls },
    ] : null,
    "buyuk-resim": sc ? [
      { l: "Bizim oy", v: t.bize, good: true }, { l: "Karşı oy", v: t.karsi, good: false },
      { l: "Yetki belgesi", v: tg ? `${tg.belge.current}/${tg.belge.target}` : "—" },
    ] : null,
    "komite-analizleri": sc ? [
      { l: "Komite", v: t.n }, { l: "Ortalama sağlık", v: `%${t.avgHealth}` },
      { l: "Ulaşılan", v: tg ? `${tg.votes.current}/${tg.votes.target}` : "—" },
    ] : null,
    verimlilik: sc ? [
      { l: "Kazanılan meclis üyeliği", v: `${t.wonSeats}/${t.seats}`, good: true },
      { l: "Çoğunluk", v: t.majority }, { l: "Kalan koltuk", v: Math.max(t.majority - t.wonSeats, 0), good: false },
    ] : null,
    kiyasla: sc ? [
      { l: "Kazanılan komite", v: `${t.won}/${t.n}`, good: true },
      { l: "Net fark", v: t.bize - t.karsi > 0 ? `+${t.bize - t.karsi}` : `${t.bize - t.karsi}`, good: t.bize >= t.karsi },
    ] : null,
    "zaman-tuneli": tg ? [
      { l: "Seçime kalan", v: `${tg.days_left} gün` },
      { l: "Durum", v: tg.votes.behind || tg.belge.behind ? "Hedefin gerisinde" : "Hedefte", good: !(tg.votes.behind || tg.belge.behind) },
    ] : null,
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="analytics-overview">
      {tabs.map((tab) => {
        const stats = STATS[tab.slug];
        return (
          <button key={tab.slug} type="button" onClick={() => onOpen(tab.slug)} data-testid={`analytics-card-${tab.slug}`}
            className="text-left rounded-xl border border-border bg-card p-4 hover:border-[#0F2C59] hover:shadow-md transition-all group">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-lg grid place-items-center bg-[#0F2C59]/10 text-[#0F2C59] dark:text-sky-300 shrink-0">
                <tab.icon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{tab.label}</p>
                <p className="text-[11px] text-muted-foreground truncate">{tab.desc}</p>
              </div>
              <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground group-hover:text-[#0F2C59] dark:group-hover:text-sky-300 shrink-0" />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {stats ? stats.map((s) => (
                <div key={s.l}>
                  <p className={`text-lg font-bold tabular-nums ${s.good === true ? "text-emerald-600" : s.good === false ? "text-red-600" : "text-foreground"}`}>{s.v}</p>
                  <p className="text-[10px] text-muted-foreground leading-tight">{s.l}</p>
                </div>
              )) : <Skeleton className="h-10 w-full col-span-3" />}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default function AnalyticsHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { tab } = useParams();
  const tabs = analyticsTabsFor(user);
  const isOverview = !tab || tab === "ozet";
  const active = tabs.find((t) => t.slug === tab);

  if (!tabs.length) {
    return <p className="text-sm text-muted-foreground" data-testid="analytics-no-access">Bu bölüm için yetkiniz yok.</p>;
  }
  const Active = active?.Comp;

  const strip = [{ slug: "ozet", label: "Özet", icon: LayoutGrid, desc: "Tüm analizlerin özeti" }, ...tabs];
  const current = isOverview ? strip[0] : active || strip[0];

  return (
    <div className="space-y-5" data-testid="analytics-hub">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl grid place-items-center bg-[#0F2C59]/10 text-[#0F2C59] dark:text-sky-300">
          <BarChart3 className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#0F2C59] dark:text-sky-300">Analizler</h1>
          <p className="text-sm text-muted-foreground">Tüm analiz ve değerlendirmeler tek ekranda</p>
        </div>
      </div>

      <div className="sticky top-0 z-20 -mx-1 px-1 pb-1 pt-1 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar" data-testid="analytics-tabs">
          {strip.map((t) => {
            const on = t.slug === current.slug;
            return (
              <button
                key={t.slug}
                type="button"
                onClick={() => navigate(t.slug === "ozet" ? "/analizler" : `/analizler/${t.slug}`)}
                data-testid={`analytics-tab-${t.slug}`}
                className={`group flex items-center gap-2 whitespace-nowrap rounded-lg border px-3 py-2 text-xs sm:text-sm font-medium transition-colors ${
                  on
                    ? "bg-[#0F2C59] text-white border-[#0F2C59] shadow-sm"
                    : "border-border text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <t.icon className={`h-4 w-4 ${on ? "" : "text-muted-foreground group-hover:text-foreground"}`} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
        <p className="mt-1.5 text-[11px] text-muted-foreground px-1" data-testid="analytics-tab-desc">{current.desc}</p>
      </div>

      {isOverview || !Active ? (
        <Overview tabs={tabs} onOpen={(slug) => navigate(`/analizler/${slug}`)} />
      ) : (
        <div key={active.slug} data-testid={`analytics-panel-${active.slug}`}>
          <Active />
        </div>
      )}
    </div>
  );
}
